import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePractice } from '../context/PracticeContext';
import { useAuth } from '../context/AuthContext';
import { resolveNavForRole, ADMIN_NAV_CONFIG } from '../config/navigation';
import { useNavAnalytics } from '../hooks/useNavAnalytics';
import api from '../services/api';
import './CommandPalette.css';

const MAX_RESULTS = 12;
const PATIENT_SEARCH_DEBOUNCE_MS = 200;

function scoreMatch(query, text) {
  if (!query) return 1;
  const q = query.toLowerCase();
  const t = (text || '').toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  const words = t.split(/\s+/);
  if (words.some(w => w.startsWith(q))) return 50;
  return 0;
}

function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { selectedPracticeId } = usePractice();
  const { user, hasPermission } = useAuth();
  const { track } = useNavAnalytics();
  const [query, setQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const activeRole = user?.activeContext?.role || user?.user?.role || null;

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setPatients([]);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !query || query.length < 2 || !selectedPracticeId) {
      setPatients([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const result = await api.getPatients(selectedPracticeId, { search: query, limit: 5 });
        setPatients(Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []));
      } catch {
        setPatients([]);
      }
    }, PATIENT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, selectedPracticeId, open]);

  const navItems = useMemo(() => {
    const practiceCfg = resolveNavForRole(activeRole) || [];
    const adminCfg = activeRole === 'super_admin' ? ADMIN_NAV_CONFIG : [];
    const flat = [];
    for (const section of [...practiceCfg, ...adminCfg]) {
      for (const item of section.items) {
        if (item.permission && !hasPermission(item.permission)) continue;
        flat.push({
          kind: 'nav',
          id: `nav:${item.path}`,
          label: item.label,
          hint: section.label,
          path: item.path,
        });
      }
    }
    return flat;
  }, [activeRole, hasPermission]);

  const results = useMemo(() => {
    const scored = [];
    for (const item of navItems) {
      const s = Math.max(scoreMatch(query, item.label), scoreMatch(query, item.hint) * 0.6);
      if (s > 0) scored.push({ ...item, _score: s });
    }
    for (const p of patients) {
      const name = `${p.first_name || ''} ${p.last_name || ''}`.trim();
      const s = Math.max(scoreMatch(query, name), scoreMatch(query, p.email || ''), scoreMatch(query, p.phone || ''));
      if (s > 0 || !query) {
        scored.push({
          kind: 'patient',
          id: `patient:${p.id}`,
          label: name || `Patient #${p.id}`,
          hint: p.email || p.phone || '',
          path: `/practice-view/patients/${p.id}`,
          _score: (s || 30) + 15, // give patients a slight bump when query matches
        });
      }
    }
    scored.sort((a, b) => b._score - a._score);
    return scored.slice(0, MAX_RESULTS);
  }, [query, navItems, patients]);

  const selectResult = useCallback((idx) => {
    const r = results[idx];
    if (!r) return;
    try {
      track('command_palette_select', {
        query,
        kind: r.kind,
        target: r.path,
      });
    } catch {}
    navigate(r.path);
    onClose();
  }, [results, navigate, onClose, query, track]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectResult(activeIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  return (
    <div className="command-palette__backdrop" onClick={onClose}>
      <div className="command-palette" onClick={e => e.stopPropagation()} role="dialog" aria-label="Command palette">
        <div className="command-palette__input-row">
          <span className="command-palette__hint">⌘K</span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pages, patients…"
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={onKeyDown}
            className="command-palette__input"
          />
        </div>
        <ul ref={listRef} className="command-palette__results">
          {results.length === 0 && (
            <li className="command-palette__empty">No matches</li>
          )}
          {results.map((r, idx) => (
            <li
              key={r.id}
              data-idx={idx}
              className={`command-palette__result ${idx === activeIndex ? 'command-palette__result--active' : ''}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => selectResult(idx)}
            >
              <span className={`command-palette__kind command-palette__kind--${r.kind}`}>{r.kind}</span>
              <span className="command-palette__label">{r.label}</span>
              {r.hint && <span className="command-palette__hint-text">{r.hint}</span>}
            </li>
          ))}
        </ul>
        <div className="command-palette__footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>enter</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
